import React, { useContext, useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, Dialog, Grow, TextField, Tooltip, createFilterOptions } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CreateNewFolderRoundedIcon from "@mui/icons-material/CreateNewFolderRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import AutoFixHighRoundedIcon from "@mui/icons-material/AutoFixHighRounded";
import CodeRoundedIcon from "@mui/icons-material/CodeRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import BusinessCenterRoundedIcon from "@mui/icons-material/BusinessCenterRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { ErrorMessage, Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { ModelPageContext } from "../../context/ModelPageContext";
import { ProjectContext } from "../../containers/Dashboard";
import { fetchAllProjects } from "../../common/Redux/Reducer";
import API_BASE_URL from "../../components/config/appConfig";
import { useToast } from "../Toast";
import {
  PROJECT_AVATAR_ICONS,
  PROJECT_AVATAR_COLORS,
  DEFAULT_PROJECT_AVATAR,
  ProjectAvatar,
  gradientOf,
} from "../../utilities/projectAvatars";
import "./createProjectModal.scss";

/* --------------------------------------------------------------------------
   Create project dialog — the same family as the create-issue and confirm
   dialogs: light header with a brand tile, bordered sections, pill actions.
   -------------------------------------------------------------------------- */

const SmoothGrow = React.forwardRef(function SmoothGrow(props, ref) {
  return <Grow ref={ref} timeout={{ enter: 320, exit: 200 }} {...props} />;
});

/* The server stores the type as free text; these are the values the existing
   projects already use, with friendlier labels. */
export const PROJECT_TYPES = [
  { value: "Company-Managed-Software", label: "Company-managed software", hint: "Shared workflows set by admins", Icon: CodeRoundedIcon },
  { value: "Team-Managed-Software", label: "Team-managed software", hint: "The team runs its own workflow", Icon: GroupsRoundedIcon },
  { value: "Business", label: "Business", hint: "Marketing, HR, finance and operations", Icon: BusinessCenterRoundedIcon },
  { value: "Service-Management", label: "Service management", hint: "Requests, incidents and support", Icon: SupportAgentRoundedIcon },
];

/** "Mobile banking app" → "MBA"; one word → its first four letters. */
export const suggestKey = (title = "") => {
  const words = String(title).toUpperCase().replace(/[^A-Z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  if (words.length === 1) return words[0].slice(0, 4);
  return words.slice(0, 5).map((word) => word[0]).join("");
};

/** Mirrors the server's slugify closely enough to preview the board link. */
export const boardSlug = (title = "") =>
  String(title).trim().replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-");

const cleanKey = (value = "") => value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);

const projectSchema = Yup.object({
  title: Yup.string().trim().required("Give the project a name").max(80, "Keep the name under 80 characters"),
  key: Yup.string()
    .trim()
    .required("Add a short project key")
    .matches(/^[A-Z][A-Z0-9]{1,9}$/, "2–10 letters or numbers, starting with a letter"),
  type: Yup.string().required("Pick a project type"),
  lead: Yup.string().required("Choose a project owner"),
});

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("accessToken")}` });

const readMe = () => {
  try {
    const token = localStorage.getItem("accessToken");
    return token ? jwtDecode(token) : {};
  } catch (error) {
    return {};
  }
};

const hueOf = (text = "") => {
  let hue = 0;
  for (let i = 0; i < text.length; i += 1) hue = (hue * 31 + text.charCodeAt(i)) % 360;
  return hue;
};

/**
 * Someone's avatar: their uploaded photo when they have one, otherwise the
 * initials on a hue derived from their name. `image` is the data URL stored on
 * the user record, so there is nothing to fetch.
 */
const Avatar = ({ name = "", size = 28, image = "" }) => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  const initials = parts.length ? `${parts[0][0]}${parts.length > 1 ? parts[parts.length - 1][0] : ""}`.toUpperCase() : "?";
  const hue = hueOf(name);
  return (
    <span
      className="pj-cp-avatar"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.4),
        background: image ? "none" : `linear-gradient(135deg, hsl(${hue} 68% 56%), hsl(${(hue + 38) % 360} 70% 40%))`,
      }}
      aria-hidden="true"
    >
      {image ? <img src={image} alt="" className="pj-cp-avatar__img" /> : initials}
    </span>
  );
};

/**
 * Pick the mark the project is shown with everywhere it is listed.
 *
 * Only the two ids are held in the form; the mark itself is drawn from the
 * shared catalogue, so the preview here is exactly what the projects table and
 * the sidebar will render.
 */
const AvatarPicker = ({ value, title, onChange }) => (
  <div className="pj-cp-avatarpick">
    <div className="pj-cp-avatarpick__preview">
      <ProjectAvatar avatar={value} title={title} className="pj-cp-avatarpick__mark" />
      <span className="pj-cp-avatarpick__caption">
        <strong>{title || "Your project"}</strong>
        <small>Shown on the projects list and in the sidebar</small>
      </span>
    </div>

    <div className="pj-cp-avatarpick__swatches" role="group" aria-label="Avatar colour">
      {PROJECT_AVATAR_COLORS.map((color) => (
        <Tooltip key={color.id} title={color.label}>
          <button
            type="button"
            className={`pj-cp-swatch ${value.color === color.id ? "is-on" : ""}`}
            style={{ background: gradientOf(color) }}
            onClick={() => onChange({ ...value, color: color.id })}
            aria-label={color.label}
            aria-pressed={value.color === color.id}
          />
        </Tooltip>
      ))}
    </div>

    <div className="pj-cp-avatarpick__grid" role="group" aria-label="Avatar icon">
      {PROJECT_AVATAR_ICONS.map((option) => {
        const Icon = option.Icon;
        const selected = value.icon === option.id;
        return (
          <Tooltip key={option.id} title={option.label}>
            <button
              type="button"
              className={`pj-cp-glyph ${selected ? "is-on" : ""}`}
              onClick={() => onChange({ ...value, icon: option.id })}
              aria-label={option.label}
              aria-pressed={selected}
            >
              <Icon />
            </button>
          </Tooltip>
        );
      })}
    </div>
  </div>
);

/* keeps the key in step with the name until someone edits the key themselves */
const KeyAutofill = ({ keyEdited }) => {
  const { values, setFieldValue } = useFormikContext();
  useEffect(() => {
    if (!keyEdited) setFieldValue("key", suggestKey(values.title), false);
  }, [values.title, keyEdited, setFieldValue]);
  return null;
};

/* whoever creates the project owns it unless they pick someone else */
const DefaultOwner = ({ users, me }) => {
  const { values, setFieldValue } = useFormikContext();
  useEffect(() => {
    if (values.lead || !users.length) return;
    const mine = users.find((user) => user.id === me?.id) || users.find((user) => me?.email && user.email === me.email);
    if (mine) setFieldValue("lead", mine.id, false);
  }, [users, me, values.lead, setFieldValue]);
  return null;
};

const filterPeople = createFilterOptions({ stringify: (option) => `${option.name} ${option.email}` });

const ModalPage = () => {
  const modal = useContext(ModelPageContext);
  const projectContext = useContext(ProjectContext) || {};
  const dispatch = useDispatch();
  const toast = useToast();
  const open = Boolean(modal?.modalState?.open);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [keyEdited, setKeyEdited] = useState(false);
  const me = useMemo(readMe, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setKeyEdited(false);
    let cancelled = false;
    (async () => {
      setUsersLoading(true);
      try {
        const { data } = await axios.get(`${API_BASE_URL}/users`, { headers: authHeaders() });
        if (!cancelled) {
          setUsers(
            (Array.isArray(data?.users) ? data.users : []).map((user) => ({
              id: user._id,
              name: user.name || user.email || "Unnamed user",
              email: user.email || "",
              image: user.profileImage || "",
            }))
          );
        }
      } catch (error) {
        console.log(error);
        if (!cancelled) toast.error("Could not load people for the owner list");
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // loads each time the dialog opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    modal.setModalState({ open: false, createProject: false });
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const payload = {
      title: values.title.trim(),
      key: values.key.trim(),
      type: values.type,
      lead: values.lead,
      avatar: values.avatar,
    };
    try {
      const { data } = await axios.post(`${API_BASE_URL}/projects/createProject`, payload, { headers: authHeaders() });
      projectContext.setProjectsList?.([
        ...(projectContext.projectsList || []),
        { id: data._id, title: data.title, key: data.key, type: data.type, lead: data.lead, avatar: data.avatar },
      ]);
      await axios.post(`${API_BASE_URL}/backlogs/createBacklog`, { projectId: data._id });
      toast.success(`${data.title || payload.title} is ready`);
      resetForm();
      handleClose();
      dispatch(fetchAllProjects(authHeaders()));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not create the project. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // a stray click outside should not throw away a half-filled form
        if (reason !== "backdropClick") handleClose();
      }}
      maxWidth={false}
      TransitionComponent={SmoothGrow}
      PaperProps={{ className: "pj-cp", "aria-labelledby": "create-project-title" }}
      slotProps={{ backdrop: { className: "pj-cp-backdrop" } }}
    >
      <Formik
        initialValues={{ title: "", key: "", type: PROJECT_TYPES[0].value, lead: "", avatar: DEFAULT_PROJECT_AVATAR }}
        validationSchema={projectSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleChange, handleBlur, setFieldValue, setFieldTouched, isSubmitting }) => {
          const owner = users.find((user) => user.id === values.lead) || null;
          /* the token has no photo on it, so the signed-in user's avatar comes
             off the loaded people list */
          const signedIn =
            users.find((user) => user.id === me?.id) ||
            users.find((user) => me?.email && user.email === me.email) ||
            null;
          const type = PROJECT_TYPES.find((option) => option.value === values.type) || null;
          const slug = boardSlug(values.title);

          return (
            <Form className="pj-cp-form" noValidate>
              <KeyAutofill keyEdited={keyEdited} />
              <DefaultOwner users={users} me={me} />

              <header className="pj-cp-head">
                <span className="pj-cp-tile" aria-hidden="true">
                  <CreateNewFolderRoundedIcon />
                </span>
                <div className="pj-cp-heading">
                  <h2 id="create-project-title">Create new project</h2>
                  <p>A home for your team's sprints, backlog and reports.</p>
                </div>
                {signedIn || me?.name || me?.email ? (
                  <Tooltip title={`Signed in as ${signedIn?.name || me?.name || me?.email}`}>
                    <span className="pj-cp-me">
                      <Avatar
                        name={signedIn?.name || me?.name || me?.email || ""}
                        size={26}
                        image={signedIn?.image}
                      />
                      <span className="pj-cp-me__text">
                        <small>Creating as</small>
                        <strong>{signedIn?.name || me?.name || me?.email}</strong>
                      </span>
                    </span>
                  </Tooltip>
                ) : null}

                <Tooltip title="Close">
                  <button type="button" className="pj-cp-close" onClick={handleClose} aria-label="Close">
                    <CloseRoundedIcon />
                  </button>
                </Tooltip>
              </header>

              <div className="pj-cp-body">
                <section className="pj-cp-section" style={{ "--d": "40ms" }}>
                  <h3 className="pj-cp-section__title">
                    <span className="pj-cp-section__icon"><BadgeRoundedIcon /></span>
                    Basics
                  </h3>

                  {/* name and key read as one line of identity, so they share a row */}
                  <div className="pj-cp-row">
                  <div className="pj-cp-field">
                    <label className="pj-cp-label" htmlFor="pj-cp-title">
                      Project name<span className="pj-cp-req">*</span>
                    </label>
                    <TextField
                      id="pj-cp-title"
                      name="title"
                      className="pj-cp-input"
                      fullWidth
                      autoFocus
                      placeholder="e.g. Mobile banking app"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inputProps={{ maxLength: 80 }}
                    />
                    <ErrorMessage name="title" component="span" className="pj-cp-error" />
                    {slug ? (
                      <span className="pj-cp-help">
                        <LinkRoundedIcon />
                        Board link: <code>/projects/{slug}</code>
                      </span>
                    ) : null}
                  </div>

                  <div className="pj-cp-field">
                    <label className="pj-cp-label" htmlFor="pj-cp-key">
                      Project key<span className="pj-cp-req">*</span>
                      {!keyEdited && values.key ? (
                        <span className="pj-cp-auto">
                          <AutoFixHighRoundedIcon />
                          Suggested
                        </span>
                      ) : null}
                    </label>
                    <TextField
                      id="pj-cp-key"
                      name="key"
                      className="pj-cp-input pj-cp-input--key"
                      fullWidth
                      placeholder="MBA"
                      value={values.key}
                      onChange={(event) => {
                        setKeyEdited(true);
                        setFieldValue("key", cleanKey(event.target.value));
                      }}
                      onBlur={handleBlur}
                      inputProps={{ maxLength: 10, "aria-describedby": "pj-cp-key-help" }}
                    />
                    <ErrorMessage name="key" component="span" className="pj-cp-error" />
                    <span className="pj-cp-help" id="pj-cp-key-help">
                      <span>
                        Issues will be numbered <b>{values.key || "KEY"}-1</b>, <b>{values.key || "KEY"}-2</b>…
                      </span>
                      {keyEdited && values.title ? (
                        <button
                          type="button"
                          className="pj-cp-link"
                          onClick={() => {
                            setKeyEdited(false);
                            setFieldValue("key", suggestKey(values.title));
                          }}
                        >
                          Use suggestion
                        </button>
                      ) : null}
                    </span>
                  </div>
                  </div>
                </section>

                <section className="pj-cp-section" style={{ "--d": "100ms" }}>
                  <h3 className="pj-cp-section__title">
                    <span className="pj-cp-section__icon"><PaletteRoundedIcon /></span>
                    Avatar
                  </h3>

                  <div className="pj-cp-field">
                    <label className="pj-cp-label">Project avatar</label>
                    <AvatarPicker
                      value={values.avatar}
                      title={values.title}
                      onChange={(next) => setFieldValue("avatar", next)}
                    />
                  </div>
                </section>

                <section className="pj-cp-section" style={{ "--d": "160ms" }}>
                  <h3 className="pj-cp-section__title">
                    <span className="pj-cp-section__icon"><TuneRoundedIcon /></span>
                    Setup
                  </h3>

                  <div className="pj-cp-field">
                    <label className="pj-cp-label" htmlFor="pj-cp-type">
                      Project type<span className="pj-cp-req">*</span>
                    </label>
                    <Autocomplete
                      id="pj-cp-type"
                      className="pj-cp-select"
                      options={PROJECT_TYPES}
                      value={type}
                      onChange={(event, next) => setFieldValue("type", next ? next.value : "")}
                      onBlur={() => setFieldTouched("type", true)}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(option, selected) => option.value === selected.value}
                      disableClearable
                      openOnFocus
                      autoHighlight
                      popupIcon={<ExpandMoreRoundedIcon />}
                      slotProps={{ paper: { className: "pj-cp-pop" } }}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...rest } = props;
                        const Icon = option.Icon;
                        return (
                          <li key={key} {...rest} className={`${rest.className || ""} pj-cp-opt`}>
                            <span className="pj-cp-opt__icon"><Icon /></span>
                            <span className="pj-cp-opt__text">
                              <strong>{option.label}</strong>
                              <small>{option.hint}</small>
                            </span>
                            {selected ? <CheckRoundedIcon className="pj-cp-opt__check" /> : null}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Choose a type"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: type ? (
                              <span className="pj-cp-lead-icon"><type.Icon /></span>
                            ) : null,
                          }}
                        />
                      )}
                    />
                    <ErrorMessage name="type" component="span" className="pj-cp-error" />
                  </div>

                  <div className="pj-cp-field">
                    <label className="pj-cp-label" htmlFor="pj-cp-owner">
                      Project owner<span className="pj-cp-req">*</span>
                    </label>
                    <Autocomplete
                      id="pj-cp-owner"
                      className="pj-cp-select"
                      options={users}
                      value={owner}
                      loading={usersLoading}
                      onChange={(event, next) => setFieldValue("lead", next ? next.id : "")}
                      onBlur={() => setFieldTouched("lead", true)}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, selected) => option.id === selected.id}
                      filterOptions={filterPeople}
                      openOnFocus
                      autoHighlight
                      popupIcon={<ExpandMoreRoundedIcon />}
                      noOptionsText="No one found"
                      loadingText="Loading people…"
                      slotProps={{ paper: { className: "pj-cp-pop" } }}
                      renderOption={(props, option, { selected }) => {
                        const { key, ...rest } = props;
                        return (
                          <li key={key} {...rest} className={`${rest.className || ""} pj-cp-opt`}>
                            <Avatar name={option.name} size={30} image={option.image} />
                            <span className="pj-cp-opt__text">
                              <strong>
                                {option.name}
                                {option.id === me?.id ? <em className="pj-cp-you">You</em> : null}
                              </strong>
                              {option.email ? <small>{option.email}</small> : null}
                            </span>
                            {selected ? <CheckRoundedIcon className="pj-cp-opt__check" /> : null}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search by name or email"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: owner ? (
                              <span className="pj-cp-lead-icon pj-cp-lead-icon--avatar">
                                <Avatar name={owner.name} size={24} image={owner.image} />
                              </span>
                            ) : null,
                          }}
                        />
                      )}
                    />
                    <ErrorMessage name="lead" component="span" className="pj-cp-error" />
                  </div>
                </section>
              </div>

              <footer className="pj-cp-foot">
                <span className="pj-cp-hint">
                  <span className="pj-cp-req">*</span> Required
                </span>
                <Button type="button" className="pj-cp-btn pj-cp-btn--ghost" onClick={handleClose} disabled={isSubmitting} disableRipple>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="pj-cp-btn pj-cp-btn--primary"
                  disabled={isSubmitting}
                  disableRipple
                  startIcon={isSubmitting ? <span className="pj-cp-spinner" /> : <AddRoundedIcon />}
                >
                  {isSubmitting ? "Creating…" : "Create project"}
                </Button>
              </footer>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default ModalPage;
